import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** A modifier option the guest selected for a line (mirrors the POS DraftModifier
 *  and the dhk8-pos-orders AddItem `modifiers[]` shape). */
export interface CartLineModifier {
  modifierId: string
  name: string
  priceDeltaCents: number
}

export interface CartLine {
  /** Unique per cart line — two units of the same product with different
   *  modifiers are distinct lines, so we can't key by productId alone. */
  lineId: string
  productId: string
  productName: string
  /** Base product price (sin deltas). El backend de orders suma los deltas:
   *  lineGross = (unitPriceCents + Σ modifier.priceDeltaCents) * quantity. */
  unitPriceCents: number
  quantity: number
  modifiers: CartLineModifier[]
  notes?: string
}

/** Per-unit price including modifier deltas — para mostrar en UI y subtotales. */
export function lineUnitWithModifiers(line: CartLine): number {
  return line.unitPriceCents + line.modifiers.reduce((s, m) => s + m.priceDeltaCents, 0)
}

interface CartState {
  lines: CartLine[]
  add: (line: Omit<CartLine, 'lineId' | 'quantity'> & { quantity?: number }) => void
  setQuantity: (lineId: string, qty: number) => void
  remove: (lineId: string) => void
  clear: () => void
  subtotalCents: () => number
}

/** Two lines merge only if same product, same notes, same set of modifiers. */
function sameLine(a: CartLine, b: Omit<CartLine, 'lineId' | 'quantity'>): boolean {
  if (a.productId !== b.productId) return false
  if ((a.notes ?? '') !== (b.notes ?? '')) return false
  if (a.modifiers.length !== b.modifiers.length) return false
  const aIds = a.modifiers.map((m) => m.modifierId).slice().sort()
  const bIds = b.modifiers.map((m) => m.modifierId).slice().sort()
  return aIds.every((id, i) => id === bIds[i])
}

function newLineId(): string {
  return `ln_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      lines: [],
      add: (line) => {
        const lines = [...get().lines]
        const qty = line.quantity ?? 1
        const existing = lines.find((l) => sameLine(l, line))
        if (existing) {
          existing.quantity += qty
        } else {
          lines.push({ ...line, lineId: newLineId(), quantity: qty, modifiers: line.modifiers ?? [] })
        }
        set({ lines })
      },
      setQuantity: (lineId, qty) => {
        const lines = get().lines
          .map((l) => (l.lineId === lineId ? { ...l, quantity: qty } : l))
          .filter((l) => l.quantity > 0)
        set({ lines })
      },
      remove: (lineId) =>
        set({ lines: get().lines.filter((l) => l.lineId !== lineId) }),
      clear: () => set({ lines: [] }),
      subtotalCents: () =>
        get().lines.reduce((acc, l) => acc + lineUnitWithModifiers(l) * l.quantity, 0),
    }),
    {
      name: 'dhk8-guest-cart',
      version: 2,
      // v1 cart lines lacked lineId/modifiers — drop stale carts cleanly.
      migrate: () => ({ lines: [] }),
    },
  ),
)
